/**
 * Manages user-customisable field configuration for the plot.
 * Per-field settings (visible, panel row) are persisted in localStorage
 * keyed by table ID so each data format gets independent preferences.
 */

/** Per-field user preferences */
export interface FieldUserConfig {
	/** Whether to render this field's trace */
	visible: boolean;
	/** 0-based panel row index (matches the domain array index from calculateDomains) */
	panel: number;
}

/** Full config for one table: fieldName → FieldUserConfig */
export type TableUserConfig = Record<string, FieldUserConfig>;

const STORAGE_KEY_PREFIX = "aws-plot-field-config-";
const STATION_KEY_PREFIX = `${STORAGE_KEY_PREFIX}station-`;
const TABLE_KEY_PREFIX = `${STORAGE_KEY_PREFIX}table-`;

function parseConfig(raw: string | null): TableUserConfig | null {
	if (!raw) return null;
	try {
		return JSON.parse(raw) as TableUserConfig;
	} catch {
		return null;
	}
}

function getStorage(): Storage | null {
	if (typeof globalThis.localStorage !== "undefined") {
		return globalThis.localStorage;
	}
	if (
		typeof window !== "undefined" &&
		typeof window.localStorage !== "undefined"
	) {
		return window.localStorage;
	}
	return null;
}

function getLegacyTableKey(tableId: number): string {
	// Backward compatibility with previously table-scoped configs.
	return `${STORAGE_KEY_PREFIX}${tableId}`;
}

function getStationKey(stationId: string): string {
	return `${STATION_KEY_PREFIX}${stationId}`;
}

function getTableDefaultKey(tableId: number): string {
	return `${TABLE_KEY_PREFIX}${tableId}`;
}

/**
 * Resolves config with hybrid precedence:
 * 1) Station-specific override
 * 2) Table-type default
 * 3) Legacy table-scoped key (read-only fallback)
 */
export function loadResolvedUserConfig(
	stationId: string,
	tableId: number,
): TableUserConfig | null {
	const storage = getStorage();
	if (!storage) return null;
	return (
		parseConfig(storage.getItem(getStationKey(stationId))) ??
		parseConfig(storage.getItem(getTableDefaultKey(tableId))) ??
		parseConfig(storage.getItem(getLegacyTableKey(tableId)))
	);
}

export function saveStationUserConfig(
	stationId: string,
	config: TableUserConfig,
): void {
	getStorage()?.setItem(getStationKey(stationId), JSON.stringify(config));
}

export function resetStationUserConfig(stationId: string): void {
	getStorage()?.removeItem(getStationKey(stationId));
}

export function saveTableDefaultConfig(
	tableId: number,
	config: TableUserConfig,
): void {
	getStorage()?.setItem(getTableDefaultKey(tableId), JSON.stringify(config));
}

export function loadTableDefaultConfig(
	tableId: number,
): TableUserConfig | null {
	const storage = getStorage();
	if (!storage) return null;
	return parseConfig(storage.getItem(getTableDefaultKey(tableId)));
}

export function resetTableDefaultConfig(tableId: number): void {
	getStorage()?.removeItem(getTableDefaultKey(tableId));
}

export function resetLegacyTableConfig(tableId: number): void {
	getStorage()?.removeItem(getLegacyTableKey(tableId));
}

export function resetAllUserConfig(stationId: string, tableId: number): void {
	const storage = getStorage();
	if (!storage) return;
	storage.removeItem(getStationKey(stationId));
	storage.removeItem(getTableDefaultKey(tableId));
	storage.removeItem(getLegacyTableKey(tableId));
}
